import axios, { AxiosResponse } from 'axios'
import { PopulationParam } from 'common/constants/PopulationParam'
import { BlogPromiseType } from 'common/types/BlogPromiseType'
import Env from 'Env'
import HTTPMethod from 'http-method-enum'
import { Converters } from './Converters'

export class BlogPromiseClass implements BlogPromiseType {
  constructor() {}

  async Posts() {
    return axios({
      method: HTTPMethod.GET,
      baseURL: Env.ApiUrl,
      url: '/blog-posts?',
      params: { ...PopulationParam, 'sort[0]': 'publishedAt:desc' },
    })
  }

  async PostsByCategory(slug: string) {
    return axios({
      method: HTTPMethod.GET,
      baseURL: Env.ApiUrl,
      url: '/blog-posts',
      params: { ...PopulationParam, 'filters[Category][Slug][$eq]': slug },
    })
  }

  async BlogPostCategories() {
    return axios({
      method: HTTPMethod.GET,
      baseURL: Env.ApiUrl,
      url: '/blog-post-categories',
      params: { publicationState: 'live' },
    })
  }

  async PostBySlug(slug: string, isPreview?: boolean) {
    const postId = await this._getPostIdFromSlug(slug, isPreview)
    return this._getPostById(postId, isPreview)
  }

  async SupportTopicBySlug(slug: string, isPreview?: boolean) {
    const topicId = await this._getSupportTopicIdFromSlug(slug, isPreview)
    return this._getSupportTopicById(topicId, isPreview)
  }

  async SupportPosts() {
    return axios({
      method: HTTPMethod.GET,
      baseURL: Env.ApiUrl,
      url: '/support-posts',
      params: PopulationParam,
    })
  }

  async SupportPostBySlug(slug: string, isPreview?: boolean) {
    const postId = await this._getSupportPostIdFromSlug(slug, isPreview)
    return this._getSupportPostById(postId, isPreview)
  }

  async SupportTopics() {
    return axios({
      method: HTTPMethod.GET,
      baseURL: Env.ApiUrl,
      url: '/support-topics',
      params: { populate: ['Icon', 'Posts'] },
    })
  }

  async SupportPostsByTitle(title: string) {
    return axios({
      method: HTTPMethod.GET,
      baseURL: Env.ApiUrl,
      url: `/support-posts?filters[Title][$containsi]=${this._formatTitle(title)}`,
    })
  }

  // Private Methods
  private async _getSupportTopicById(id: number, isPreview?: boolean) {
    if (isPreview) {
      return axios({
        method: HTTPMethod.GET,
        baseURL: Env.ApiUrl,
        url: `/support-topics/${id}`,
        params: { ...PopulationParam, publicationState: 'preview' },
      })
    }
    return axios({
      method: HTTPMethod.GET,
      baseURL: Env.ApiUrl,
      url: `/support-topics/${id}`,
      params: PopulationParam,
    })
  }

  private async _getSupportTopicIdFromSlug(slug: string, isPreview?: boolean) {
    if (isPreview) {
      return axios({
        method: HTTPMethod.GET,
        baseURL: Env.ApiUrl,
        url: `/support-topics?filters[Slug][$eq]=${slug}`,
        params: { publicationState: 'preview' },
      }).then((resp) => resp.data['data'][0]['id'])
    }

    return axios({
      method: HTTPMethod.GET,
      baseURL: Env.ApiUrl,
      url: `/support-topics?filters[Slug][$eq]=${slug}`,
    }).then((resp) => resp.data['data'][0]['id'])
  }

  private async _getPostById(id: number, isPreview?: boolean) {
    if (isPreview) {
      return axios({
        method: HTTPMethod.GET,
        baseURL: Env.ApiUrl,
        url: `/blog-posts/${id}`,
        params: { ...PopulationParam, publicationState: 'preview' },
      })
    }
    return axios({
      method: HTTPMethod.GET,
      baseURL: Env.ApiUrl,
      url: `/blog-posts/${id}`,
      params: PopulationParam,
    })
  }

  private async _getPostIdFromSlug(slug: string, isPreview?: boolean) {
    if (isPreview) {
      return axios({
        method: HTTPMethod.GET,
        baseURL: Env.ApiUrl,
        url: `/blog-posts?filters[Slug][$eq]=${slug}`,
        params: { publicationState: 'preview' },
      }).then((resp) => resp.data['data'][0]['id'])
    }

    return axios({
      method: HTTPMethod.GET,
      baseURL: Env.ApiUrl,
      url: `/blog-posts?filters[Slug][$eq]=${slug}`,
      params: { publicationState: 'live' },
    }).then((resp) => resp.data['data'][0]['id'])
  }

  private async _getSupportPostById(id: number, isPreview?: boolean) {
    if (isPreview) {
      return axios({
        method: HTTPMethod.GET,
        baseURL: Env.ApiUrl,
        url: `/support-posts/${id}`,
        params: { ...PopulationParam, publicationState: 'preview' },
      })
    }
    return axios({
      method: HTTPMethod.GET,
      baseURL: Env.ApiUrl,
      url: `/support-posts/${id}`,
      params: PopulationParam,
    })
  }

  private async _getSupportPostIdFromSlug(slug: string, isPreview?: boolean) {
    if (isPreview) {
      return axios({
        method: HTTPMethod.GET,
        baseURL: Env.ApiUrl,
        url: `/support-posts?filters[Slug][$eq]=${slug}`,
        params: { publicationState: 'preview' },
      }).then((resp) => resp.data['data'][0]['id'])
    }

    return axios({
      method: HTTPMethod.GET,
      baseURL: Env.ApiUrl,
      url: `/support-posts?filters[Slug][$eq]=${slug}`,
    }).then((resp) => resp.data['data'][0]['id'])
  }

  private _convertCase<T extends object>(input: T) {
    return Converters.KeysConvert.ToLowerCase(input)
  }

  private _formatTitle(input: string) {
    return input.trim().split(' ').join('%20')
  }
}

export const BlogPromise = new BlogPromiseClass()
